import axios, { AxiosInstance } from "axios";
import io from "socket.io-client";
import { getToken } from "@/utils/localstorage";
import { logger } from "@/helpers/tslog";
import { setSlideImagesId } from "@/utils/formatTemplate/slideImages";

interface ChartExtractionData {
  fileId: string;
}

interface ActiveExtraction {
  resolve: (value: string) => void;
  reject: (reason?: any) => void;
}

interface SocketConfig {
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  timeout: number;
  forceNew: boolean;
}

interface ApiRoutes {
  agent: {
    extract: string;
  };
}

class ChartExtractionConfig {
  private readonly apiUrl: string;
  private readonly maxExtractionTimeout: number;
  private readonly routes: ApiRoutes;

  constructor() {
    this.apiUrl = process.env.API_URL || "";
    this.maxExtractionTimeout = 900000;

    this.routes = {
      agent: {
        extract: "/agent/extract-potx-slides",
      },
    };

    this.validateConfig();
    logger.info("ChartExtractionConfig initialized");
  }

  private validateConfig(): void {
    if (!this.apiUrl) {
      logger.error("API_URL environment variable is not set");
      throw new Error("Required environment variable API_URL must be set");
    }
  }

  async getApiConfig() {
    const token = await getToken();

    return {
      baseURL: this.apiUrl,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  getSocketConfig(): SocketConfig {
    return {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      forceNew: true,
    };
  }

  getMaxExtractionTimeout(): number {
    return this.maxExtractionTimeout;
  }

  getExtractRoute(): string {
    return this.routes.agent.extract;
  }
}

class ChartExtractionSocket {
  private socket: ReturnType<typeof io> | null = null;
  private activeExtractions: Map<string, ActiveExtraction> = new Map();
  private config: ChartExtractionConfig;
  private isConnecting: boolean = false;

  constructor(config: ChartExtractionConfig) {
    this.config = config;
    logger.info("ChartExtractionSocket initialized");
  }

  private async initializeSocket(): Promise<void> {
    if (this.socket?.connected || this.isConnecting) return;

    this.isConnecting = true;
    try {
      const apiConfig = await this.config.getApiConfig();
      this.socket = io(apiConfig.baseURL, this.config.getSocketConfig());
      await this.setupSocketEventHandlers();
      logger.info("Socket initialized successfully");
    } finally {
      this.isConnecting = false;
    }
  }

  private setupSocketEventHandlers(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.socket) return resolve();

      this.socket.on("connect", () => {
        logger.info("Socket connected");
        resolve();
      });

      this.socket.on("disconnect", () => {
        logger.warn("Socket disconnected");
        // Reject pending operations if we've exceeded reconnection attempts
        if (this.socket?.io?.reconnectionAttempts() >= this.config.getSocketConfig().reconnectionAttempts) {
          logger.error("Socket connection failed after maximum reconnection attempts");
          this.rejectAllPendingExtractions(new Error("Socket connection failed after maximum reconnection attempts"));
        }
      });

      this.socket.on("error", (error: Error) => {
        logger.error("Socket error", { error: error.message });
        throw new Error(`Socket error: ${error.message}`);
      });

      this.socket.on("potxSlidesReady", this.handlePotxImageReady.bind(this));
      this.socket.on("potxSlidesError", this.handlePotxImageError.bind(this));

      this.socket.on("connect_error", (error: Error) => {
        logger.error("Socket connection error", { error: error.message });
        throw new Error(`Socket connection error: ${error.message}`);
      });
    });
  }

  private async handlePotxImageReady(data: ChartExtractionData): Promise<void> {
    logger.info("Slides images ready", { fileId: data.fileId });
    const extraction = this.activeExtractions.get(data.fileId);
    if (!extraction) {
      logger.warn("No active extraction found for documentId", { fileId: data.fileId });
      return;
    }

    try {
      setSlideImagesId(data.fileId);
      extraction.resolve(data.fileId);
      logger.info("Template Slide images successfully processed", { fileId: data.fileId });
    } catch (error) {
      logger.error("Failed to process Template Slide images", { fileId: data.fileId, error });
      extraction.reject(error);
    } finally {
      this.cleanupExtraction(data.fileId);
    }
  }

  private async handlePotxImageError(data: ChartExtractionData): Promise<void> {
    logger.error("Template Slide images error", { fileId: data.fileId });
    const extraction = this.activeExtractions.get(data.fileId);
    if (!extraction) {
      logger.warn("No active extraction found for fileId", { fileId: data.fileId });
      return;
    }
    extraction.reject(new Error("Failed to process Template Slide images"));
    this.cleanupExtraction(data.fileId);
  }

  private rejectAllPendingExtractions(error: Error): void {
    for (const extraction of Array.from(this.activeExtractions.entries())) {
      const [fileId, value] = extraction;
      value.reject(error);
      this.cleanupExtraction(fileId);
    }
  }

  private cleanupExtraction(fileId: string): void {
    const extraction = this.activeExtractions.get(fileId);
    if (extraction) {
      this.activeExtractions.delete(fileId);
    }

    if (this.activeExtractions.size === 0 && this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  async waitForChartImage(fileId: string): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.initializeSocket();
      } catch (error) {
        reject(new Error(`Failed to initialize socket: ${error instanceof Error ? error.message : "Unknown error"}`));
        return;
      }

      this.activeExtractions.set(fileId, { resolve, reject });

      this.socket?.emit("joinPotxRoom", fileId);
    });
  }
}

const config = new ChartExtractionConfig();
const extractionSocket = new ChartExtractionSocket(config);

// Utility functions for API and data handling
async function createApiClient(): Promise<AxiosInstance> {
  const config = new ChartExtractionConfig();
  return axios.create(await config.getApiConfig());
}

async function initiateExtraction(fileId: string, fileUrl: string): Promise<void> {
  logger.info("Initiating extraction for potx template fileId:", fileId);
  const api = await createApiClient();
  await api.post(config.getExtractRoute(), {
    fileId,
    fileUrl,
  });
}

// Main entry point for chart extraction
export async function extractAndGetSlidesImages(event?: Office.AddinCommands.Event): Promise<void> {
  try {
    logger.info("Starting Slide extraction process");
    await Office.onReady();

    const raw = localStorage.getItem("fileData");
    const fileData = raw ? JSON.parse(raw) : null;
    const { fileId, fileUrl } = fileData;
    logger.info("Template file saved on server", { fileId });

    await initiateExtraction(fileId, fileUrl);
    logger.info("Template Slide images extraction initiated", { fileId });
    await extractionSocket.waitForChartImage(fileId);
    localStorage.removeItem("fileData");

    if (event) {
      event.completed();
    }
  } catch (error) {
    logger.error("Template Slide images extraction failed", { error });
    if (event) {
      event.completed();
    }
  }
}

export async function fetchPotxSlideImages(fileId: string) {
  const response = await axios.get(`${process.env.API_URL}/format-images?fileId=${fileId}`, {
    headers: { Authorization: `Bearer ${await getToken()}` },
  });
  return response.data;
}

export const handleUpload = async (formatId: string, file: any) => {
  if (!file?.[0]) return;
  try {
    const formData = new FormData();
    formData.append("potxFile", file[0]);

    const response = await axios.post(`${process.env.API_URL}/format-potx-to-images`, formData, {
      headers: { Authorization: `Bearer ${await getToken()}` },
      params: { formatId },
    });

    if (response.status !== 200) {
      throw new Error("Failed to upload POTX file");
    }

    localStorage.setItem("fileData", JSON.stringify({ fileId: response.data.fileId, fileUrl: response.data.fileUrl }));
  } catch (error) {
    logger.error("Upload failed:", error);
  }
  Office.context.ui.messageParent("EXTRACT_SLIDES");
};

export function bufferToBase64Image(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const binary = bytes.reduce((str, byte) => str + String.fromCharCode(byte), "");
  const base64 = window.btoa(binary);
  return `data:image/png;base64,${base64}`;
}

export function bufferToBase64ImagePrimaryTemplate(buffer: any, contentType: string = "image/jpeg"): string {
  try {
    let bytes: Uint8Array;
    if (buffer instanceof ArrayBuffer) {
      bytes = new Uint8Array(buffer);
    } else if (buffer && buffer.data) {
      bytes = new Uint8Array(buffer.data);
    } else if (buffer && buffer.buffer) {
      bytes = new Uint8Array(buffer.buffer);
    } else if (buffer && typeof buffer === "object") {
      const bufferKeys = Object.keys(buffer);
      if (bufferKeys.includes("data")) {
        bytes = new Uint8Array(buffer.data);
      } else if (bufferKeys.includes("length")) {
        const arrayData = Array.from(buffer) as number[];
        bytes = new Uint8Array(arrayData);
      } else {
        throw new Error("Unable to extract bytes from buffer object");
      }
    } else {
      throw new Error("Invalid buffer type");
    }

    const binary = bytes.reduce((str, byte) => str + String.fromCharCode(byte), "");
    const base64 = window.btoa(binary);
    return `data:${contentType};base64,${base64}`;
  } catch (error) {
    logger.error("Error in bufferToBase64Image:", error);
    return "";
  }
}
